import { useRef } from 'react';
import { useDenseTableLabels } from '../hooks/useDenseTableLabels';
import { showConfirm } from '../components/ConfirmDialog';
import { useTranslation } from 'react-i18next';
import { useConfig, useConfigLoadError } from '../hooks/useConfig';
import { useMusicians } from '../hooks/useMusicians';
import { useCrew } from '../hooks/useCrew';
import type { SpecsConfig, InstrumentWireless, ProvidedBy, WirelessTransmitterType, TransportType } from '../types';
import { PROVIDED_BY_LABELS, mergeStageRacks } from '../types';
import ConfigPageHeader from '../components/ConfigPageHeader';
import ConfigField from '../components/ConfigField';
import ProviderSelector from '../components/ProviderSelector';
import PageLoadError from '../components/PageLoadError';
import AutoTextarea from '../components/AutoTextarea';
import Button from '../components/ui/Button';
import { useContentConfigPage } from '../hooks/useContentConfigPage';
import { useLayoutOrder } from '../hooks/useLayoutOrder';
import { sortByLayoutOrder } from '../lib/layoutOrder';
import { useRackUsage } from '../hooks/useRackUsage';
import { rackCapacities, isChannelTaken } from '../lib/rackUsage';

/**
 * Audio specs config page (v0.645) — one of the two pages split out of the
 * former `Audio & Backline` page. It owns everything AUDIO-SPEC:
 *   - Wireless Microphones (microphonesSpecs + frequencyRange + the wireless
 *     transmitters table)
 *   - Cabling (FOH / stage / audio cables)
 *   - Network & Power
 *   - Band I/O Racks
 *   - Sidefills
 *
 * The band-level Backline (items list + summary) lives on the sibling page
 * `ConfigBacklinePage`. The shared write gate / transport / focus live in
 * `useContentConfigPage`.
 */
export default function ConfigAudioSpecsPage() {
  const denseRef = useRef<HTMLDivElement | null>(null);
  useDenseTableLabels(denseRef);
  const { config, updateSpecs, updateGlobalRacks, rackWarnings, reload } = useConfig();
  // Lot 3a — the rider_config could not be read: the page would render EMPTY
  // fields as if the band had nothing typed. See PageLoadError.tsx.
  const configLoad = useConfigLoadError();
  const { canEditContent, readOnlyNote, transport, setTransport } = useContentConfigPage();
  const { usage } = useRackUsage();
  const { t } = useTranslation();
  // Reorder the editable Technical Specification sections to mirror the Rider
  // Layout's `audio-backline.*` order. I/O Racks, Sidefills and the Wireless
  // transmitters table stay in fixed order (out of the layout), as does the
  // wireless transmitters table inside its section.
  const layoutOrder = useLayoutOrder('audio-backline');
  const { musicians } = useMusicians();
  const { crew } = useCrew();

  const specs = config.specs[transport];

  // Wireless transmitters: an instrument appears here ONLY when its card has
  // "Wireless transmitter" ticked (instrument.wireless.enabled). The type,
  // model and provider (band/venue) are then set per transport preset in
  // specs.wirelessTransmitters[instrumentId]. The signal still enters wired
  // (DI/mic); the transmitter is a separate request. (2026-08-12)
  const selMus = new Set(config.rider?.activeMusicianIds || []);
  const selCrew = new Set(config.rider?.activeCrewIds || []);
  const wt = specs.wirelessTransmitters || {};

  // Only instruments that NEED a wireless link (ticked on their card), with
  // their current wireless state from the config.
  const allInstruments: { id: string; person: string; name: string; wireless: InstrumentWireless }[] = [];
  const collect = (list: any[], sel: Set<string>) => {
    for (const m of list) {
      if (!sel.has(m.id)) continue;
      for (const inst of m.instruments || []) {
        if (!inst?.wireless?.enabled) continue;
        allInstruments.push({
          id: inst.id,
          person: m.name || '?',
          name: inst.name || 'Instrument',
          wireless: wt[inst.id] || { transmitterType: 'beltpack_jack', notes: '', micProvider: 'band', receiverProvider: 'band' },
        });
      }
    }
  };
  collect(musicians, selMus);
  collect(crew, selCrew);

  // Update one instrument's wireless definition for the CURRENT transport.
  // ⚠️ SINGLE-FLAG MODEL (2026-09-29, user decision A): `enabled` is INERT — the
  // rider follows the CARD flag instrument.wireless.enabled only. So this NEVER
  // seeds `enabled` on a new entry (that default `{ enabled:false }` WAS the bug:
  // editing a type/provider created the entry pre-disabled → the transmitter
  // vanished from the rider with no UI to turn it back on) and never overwrites
  // an existing value (carried through untouched — data preserved).
  const updateWireless = (instId: string, patch: Partial<InstrumentWireless>) => {
    const current = wt[instId] || { transmitterType: 'beltpack_jack', notes: '', micProvider: 'band', receiverProvider: 'band' };
    const next = { ...current, ...patch };
    const nextWT = { ...wt, [instId]: next };
    updateSpecs({ [transport]: { ...specs, wirelessTransmitters: nextWT } } as any);
  };

  const TX_LABELS: Record<WirelessTransmitterType, string> = {
    handheld: 'configAudioBackline.handheld', beltpack_clip: 'configAudioBackline.beltpackClip', beltpack_jack: 'configAudioBackline.beltpackJack', headset: 'configAudioBackline.headset', other: 'configAudioBackline.other',
  };

  const handleSpecChange = (key: keyof SpecsConfig, value: string) => {
    updateSpecs({ [transport]: { ...specs, [key]: value } } as any);
  };

  // Per-field band/venue provider (2026-10-18): feeds the rider PDF's venue
  // highlight — a spec line whose provider is venue gets tinted. Same model as
  // iem.wedgesProvider. Default band (no highlight) for back-compat.
  type SpecLineKey = 'microphonesSpecs' | 'frequencyRange' | 'cableSpec' | 'stageCableSpec' | 'audioCablesDetails' | 'networkRequirements' | 'powerRequirements';
  const handleSpecProvider = (key: SpecLineKey, value: ProvidedBy) => {
    updateSpecs({ [transport]: { ...specs, [`${key}Provider`]: value } } as any);
  };
  const specProvider = (key: SpecLineKey): ProvidedBy =>
    ((specs as any)[`${key}Provider`] || 'band') as ProvidedBy;
  const SpecProviderRow = ({ keyName }: { keyName: SpecLineKey }) => (
    <ProviderSelector disabled={!canEditContent} value={specProvider(keyName)} onChange={(v) => handleSpecProvider(keyName, v)} />
  );

  // Page-level populate: ONE confirmation, then copy the AUDIO SPECS section
  // (Technical Specs only — the Backline items live on the sibling page) from
  // the chosen mode into the current one.
  const populateAll = async (src: TransportType) => {
    if (!(await showConfirm(t('configAudioSpecs.populateAllConfirm', { src })))) return;
    updateSpecs({ [transport]: { ...config.specs[src] } } as any);
  };

  // Only the string-valued Specs fields are editable inline / populatable.
  const populateSpecField = (key: 'microphonesSpecs' | 'frequencyRange' | 'cableSpec' | 'stageCableSpec' | 'audioCablesDetails' | 'networkRequirements' | 'powerRequirements') => async (src: TransportType) => {
    const srcVal = config.specs[src][key];
    if (srcVal === specs[key]) return;
    if (await showConfirm(`Overwrite "${key}" with the value from ${src}?`)) {
      handleSpecChange(key, srcVal);
      // The provider rides with its text (2026-10-18): copying a spec line from
      // another mode must not leave the old mode's band/venue choice behind.
      const srcProv = (config.specs[src] as any)[`${key}Provider`];
      if (srcProv && srcProv !== (specs as any)[`${key}Provider`]) handleSpecProvider(key, srcProv);
    }
  };

  // The editable Technical Specification sections are reorderable per
  // `audio-backline.*`. The wireless transmitters table stays inside the
  // Wireless Microphones section (it is not a layout field itself).
  const specBlocks = [
    {
      id: 'wireless.mics',
      render: () => (
        <div className="form-section" data-field-id="wireless.mics">
          <h2>{t('configAudioBackline.wirelessMics')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configAudioBackline.micsSpecs')} current={transport} fieldId="wireless.mics" exampleText="4x Shure ULXD1 handheld (Beta 58 heads), 2x Sennheiser EW G4… — provided by the band or requested from the venue" onUseExample={(t) => handleSpecChange('microphonesSpecs', t)} onPopulateFrom={populateSpecField('microphonesSpecs')}>
            <AutoTextarea disabled={!canEditContent}
              value={specs.microphonesSpecs || ''}
              onChange={(e) => handleSpecChange('microphonesSpecs', e.target.value)}
              placeholder="e.g. 4x Shure ULXD1 handheld (Beta 58 heads), 2x Sennheiser EW G4… — provided by the band or requested from the venue"
            />
          </ConfigField>
          <SpecProviderRow keyName="microphonesSpecs" />
          <ConfigField disabled={!canEditContent} label={t('configAudioBackline.frequencyRange')} current={transport} fieldId="wireless.frequency" exampleText="Sennheiser 2000-626 MHz, Shure ULXD" onUseExample={(t) => handleSpecChange('frequencyRange', t)} onPopulateFrom={populateSpecField('frequencyRange')}>
            <AutoTextarea disabled={!canEditContent}
              value={specs.frequencyRange}
              onChange={(e) => handleSpecChange('frequencyRange', e.target.value)}
              placeholder="e.g. Sennheiser 2000-626 MHz, Shure ULXD"
            />
          </ConfigField>
          <SpecProviderRow keyName="frequencyRange" />

          {allInstruments.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: '0.8rem', color: 'var(--text-dim)', display: 'block', marginBottom: 6 }}>
                {t('configAudioBackline.wirelessTxLabel', { transport })}
              </label>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-dim)', margin: '0 0 8px' }}>
                {t('configAudioBackline.wirelessTxHint')}
              </p>
              <div className="table-wrapper">
                <table className="tbl-dense" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>{t('configAudioBackline.colPerson')}</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>{t('configAudioBackline.colInstrument')}</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>{t('configAudioBackline.colType')}</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>{t('configAudioBackline.colModel')}</th>
                      <th style={{ textAlign: 'center', padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>{t('configAudioBackline.colMicBy')}</th>
                      <th style={{ textAlign: 'center', padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>{t('configAudioBackline.colReceiverBy')}</th>
                      <th style={{ textAlign: 'center', padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>{t('configAudioBackline.colBothByBand')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allInstruments.map((inst) => {
                      const w = inst.wireless;
                      return (
                        <tr key={inst.id}>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>{inst.person}</td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>{inst.name}</td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>
                            <select disabled={!canEditContent}
                              value={w.transmitterType || 'beltpack_jack'}
                              onChange={(e) => updateWireless(inst.id, { transmitterType: e.target.value as WirelessTransmitterType })}
                              style={{ width: 140 }}
                            >
                              {(Object.keys(TX_LABELS) as WirelessTransmitterType[]).map((tx) => (
                                <option key={tx} value={tx}>{t(TX_LABELS[tx])}</option>
                              ))}
                            </select>
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>
                            <input disabled={!canEditContent}
                              type="text"
                              value={w.notes || ''}
                              onChange={(e) => updateWireless(inst.id, { notes: e.target.value })}
                              placeholder={t('configAudioBackline.modelPh')}
                              style={{ width: 150 }}
                            />
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>
                            <select disabled={!canEditContent}
                              value={w.micProvider || 'band'}
                              onChange={(e) => updateWireless(inst.id, { micProvider: e.target.value as ProvidedBy })}
                              style={{ width: 110 }}
                            >
                              <option value="band">{PROVIDED_BY_LABELS.band}</option>
                              <option value="venue">{PROVIDED_BY_LABELS.venue}</option>
                            </select>
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>
                            <select disabled={!canEditContent}
                              value={w.receiverProvider || 'band'}
                              onChange={(e) => updateWireless(inst.id, { receiverProvider: e.target.value as ProvidedBy })}
                              style={{ width: 110 }}
                            >
                              <option value="band">{PROVIDED_BY_LABELS.band}</option>
                              <option value="venue">{PROVIDED_BY_LABELS.venue}</option>
                            </select>
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>
                            <input disabled={!canEditContent}
                              type="checkbox"
                              checked={(w.micProvider || 'band') === 'band' && (w.receiverProvider || 'band') === 'band'}
                              onChange={(e) => {
                                const both = e.target.checked ? 'band' : 'venue';
                                updateWireless(inst.id, { micProvider: both, receiverProvider: both });
                              }}
                              title={t('configAudioBackline.setBoth')}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'audio-backline.cable',
      render: () => (
        <div className="form-section" data-field-id="audio-backline.cable">
          <h2>{t('configAudioBackline.cabling')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configAudioBackline.fohCables')} current={transport} fieldId="audio-backline.cable" exampleText="2x AES50 Ethernet (CAT 5e/6/7 SFTP, EtherCon, max 75m)..." onUseExample={(t) => handleSpecChange('cableSpec', t)} onPopulateFrom={populateSpecField('cableSpec')} labelBlock>
            <AutoTextarea disabled={!canEditContent}
              value={specs.cableSpec}
              onChange={(e) => handleSpecChange('cableSpec', e.target.value)}
              placeholder="e.g. 2x AES50 Ethernet (CAT 5e/6/7 SFTP, EtherCon, max 75m)..."
              rows={3}
            />
          </ConfigField>
          <SpecProviderRow keyName="cableSpec" />
          <ConfigField disabled={!canEditContent} label={t('configAudioBackline.stageCables')} current={transport} fieldId="audio-backline.stage-cable" exampleText="2x 25m XLR looms, power looms to stage..." onUseExample={(t) => handleSpecChange('stageCableSpec', t)} onPopulateFrom={populateSpecField('stageCableSpec')} labelBlock>
            <AutoTextarea disabled={!canEditContent}
              value={specs.stageCableSpec || ''}
              onChange={(e) => handleSpecChange('stageCableSpec', e.target.value)}
              placeholder="e.g. 2x 25m XLR looms, power looms to stage..."
              rows={3}
            />
          </ConfigField>
          <SpecProviderRow keyName="stageCableSpec" />
          {/* exampleText corrigé le 2026-10-15 : il proposait du contenu DMX pour le champ
              « Audio Cables Details » (vestige du nom de l'id `audio-backline.dmx`). */}
          <ConfigField disabled={!canEditContent} label={t('configAudioBackline.audioCables')} current={transport} fieldId="audio-backline.dmx" exampleText="2x 8-way XLR looms on stage, 1x AES50 (CAT 5e SFTP, EtherCon) FOH → stage…" onUseExample={(t) => handleSpecChange('audioCablesDetails', t)} onPopulateFrom={populateSpecField('audioCablesDetails')} labelBlock>
            <AutoTextarea disabled={!canEditContent}
              value={specs.audioCablesDetails}
              onChange={(e) => handleSpecChange('audioCablesDetails', e.target.value)}
              placeholder="e.g. 2x 8-way XLR looms on stage, 1x AES50 (CAT 5e SFTP, EtherCon) FOH → stage"
              rows={3}
            />
          </ConfigField>
          <SpecProviderRow keyName="audioCablesDetails" />
        </div>
      ),
    },
    {
      id: 'audio-backline.network',
      render: () => (
        <div className="form-section" data-field-id="audio-backline.network">
          <h2>{t('configAudioBackline.networkPower')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configAudioBackline.networkReq')} current={transport} fieldId="audio-backline.network" exampleText="1Gbps managed switch, fiber optic, POE" onUseExample={(t) => handleSpecChange('networkRequirements', t)} onPopulateFrom={populateSpecField('networkRequirements')} labelBlock>
            <AutoTextarea disabled={!canEditContent}
              value={specs.networkRequirements}
              onChange={(e) => handleSpecChange('networkRequirements', e.target.value)}
              placeholder="e.g. 1Gbps managed switch, fiber optic, POE"
              rows={2}
            />
          </ConfigField>
          <SpecProviderRow keyName="networkRequirements" />
          <ConfigField disabled={!canEditContent} label={t('configAudioBackline.powerReq')} current={transport} fieldId="audio-backline.power" exampleText="230V 3-phase, 16A circuits, dedicated ground, UPS" onUseExample={(t) => handleSpecChange('powerRequirements', t)} onPopulateFrom={populateSpecField('powerRequirements')} labelBlock>
            <AutoTextarea disabled={!canEditContent}
              value={specs.powerRequirements}
              onChange={(e) => handleSpecChange('powerRequirements', e.target.value)}
              placeholder="e.g. 230V 3-phase, 16A circuits, dedicated ground, UPS"
              rows={2}
            />
          </ConfigField>
          <SpecProviderRow keyName="powerRequirements" />
        </div>
      ),
    },
  ];

  const sortedSpecBlocks = sortByLayoutOrder(specBlocks, layoutOrder, (b) => b.id);


  // Lot 3a: the rider_config could not be read → empty fields would read as
  // "this band typed nothing". Show the shared state instead; the writes stay
  // gated by `useConfig` skipSave, so a retry cannot wipe anything.
  if (configLoad.failed) {
    return (
      <PageLoadError
        title={t('configAudioSpecs.title')}
        detail={configLoad.message}
        onRetry={() => void reload().catch(() => undefined)}
      />
    );
  }

  return (
    <div ref={denseRef} className="page config-page" data-page="config-audio-specs" data-density="socle">
      <ConfigPageHeader disabled={!canEditContent}
        iconKey="configAudioSpecs.title"
        title={t('configAudioSpecs.title')}
        transport={transport}
        onTransportChange={setTransport}
        onPopulateAll={populateAll}
      />
      <p className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>{t('configAudioSpecs.subtitle')}</p>

      {/* Non-blocking rack-occupation warnings (mode AVERTIR, chantier #2).
          The save always succeeds; this banner is advisory only. */}
      {rackWarnings.length > 0 && (
        <div className="conflict-banner" role="alert">
          <strong>{t('musicians.conflictsDetected')}</strong>
          <ul>
            {rackWarnings.map((w, i) => (
              <li key={i}>{w.message}</li>
            ))}
          </ul>
        </div>
      )}

      {readOnlyNote && (
        <p className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>{t('common.readOnlyAccount')}</p>
      )}
      <div className="edit-form">
        {/* I/O Racks — named destinations for track inputs/outputs */}
        <div className="form-section">
          <h2>{t('configAudioBackline.globalRacks')}</h2>
          <p style={{ marginTop: -8, marginBottom: 12, color: 'var(--text-dim)', fontSize: '0.85rem' }}>
            {t('configAudioBackline.globalRacksHint')}
          </p>
          {(config.globalRacks || []).map((rack, rackIndex) => (
            <div key={rack.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <button
                  type="button"
                  title={t('configAudioBackline.moveUp')}
                  disabled={!canEditContent || rackIndex === 0}
                  onClick={() => {
                    const racks = [...(config.globalRacks || [])];
                    if (rackIndex === 0) return;
                    [racks[rackIndex - 1], racks[rackIndex]] = [racks[rackIndex], racks[rackIndex - 1]];
                    updateGlobalRacks(racks);
                  }}
                  style={{ border: 'none', background: 'none', cursor: rackIndex === 0 ? 'default' : 'pointer', opacity: rackIndex === 0 ? 0.3 : 1, padding: 0, lineHeight: 1 }}
                >↑</button>
                <button
                  type="button"
                  title={t('configAudioBackline.moveDown')}
                  disabled={!canEditContent || rackIndex >= (config.globalRacks || []).length - 1}
                  onClick={() => {
                    const racks = [...(config.globalRacks || [])];
                    if (rackIndex >= racks.length - 1) return;
                    [racks[rackIndex], racks[rackIndex + 1]] = [racks[rackIndex + 1], racks[rackIndex]];
                    updateGlobalRacks(racks);
                  }}
                  style={{ border: 'none', background: 'none', cursor: rackIndex >= (config.globalRacks || []).length - 1 ? 'default' : 'pointer', opacity: rackIndex >= (config.globalRacks || []).length - 1 ? 0.3 : 1, padding: 0, lineHeight: 1 }}
                >↓</button>
              </div>
              <input disabled={!canEditContent}
                type="text"
                value={rack.name}
                onChange={(e) => {
                  const racks = (config.globalRacks || []).map((r) =>
                    r.id === rack.id ? { ...r, name: e.target.value } : r
                  );
                  updateGlobalRacks(racks);
                }}
                placeholder={t('configAudioBackline.rackPh')}
                className="form-input"
                style={{ flex: 1, minWidth: 140 }}
              />
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>{t('configAudioBackline.in')}</label>
                <input disabled={!canEditContent}
                  type="number"
                  className="form-input"
                  style={{ width: 64 }}
                  value={rack.inputs?.toString() ?? ''}
                  onChange={(e) => {
                    const racks = (config.globalRacks || []).map((r) =>
                      r.id === rack.id ? { ...r, inputs: parseInt(e.target.value) || 0 } : r
                    );
                    updateGlobalRacks(racks);
                  }}
                  placeholder="16"
                  min="0"
                />
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <label style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>{t('configAudioBackline.out')}</label>
                <input disabled={!canEditContent}
                  type="number"
                  className="form-input"
                  style={{ width: 64 }}
                  value={rack.outputs?.toString() ?? ''}
                  onChange={(e) => {
                    const racks = (config.globalRacks || []).map((r) =>
                      r.id === rack.id ? { ...r, outputs: parseInt(e.target.value) || 0 } : r
                    );
                    updateGlobalRacks(racks);
                  }}
                  placeholder="8"
                  min="0"
                />
              </div>
              {/* Pad OUT per rack (2026-10-18, user): only the OUTPUT list is
                  switchable — the Input list pads its holes unconditionally. */}
              <label title={t('configAudioBackline.padOutHint')} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.72rem', color: 'var(--text-dim)' }}>
                <input
                  type="checkbox"
                  disabled={!canEditContent}
                  checked={rack.padOutputsInRider !== false}
                  onChange={(e) => {
                    const racks = (config.globalRacks || []).map((r) =>
                      r.id === rack.id ? { ...r, padOutputsInRider: e.target.checked } : r
                    );
                    updateGlobalRacks(racks);
                  }}
                />
                {t('configAudioBackline.padOut')}
              </label>
              <input disabled={!canEditContent}
                type="text"
                value={rack.notes || ''}
                onChange={(e) => {
                  const racks = (config.globalRacks || []).map((r) =>
                    r.id === rack.id ? { ...r, notes: e.target.value } : r
                  );
                  updateGlobalRacks(racks);
                }}
                placeholder={t('configAudioBackline.rackNotesPh')}
                className="form-input"
                style={{ flex: 1, minWidth: 120 }}
              />
              <Button disabled={!canEditContent}
                variant="ghost"
                size="sm"
                onClick={async () => {
                  if (!(await showConfirm('Remove this I/O rack? Tracks pointing to it will show "—".'))) return;
                  const racks = (config.globalRacks || []).filter((r) => r.id !== rack.id);
                  updateGlobalRacks(racks);
                }}
              >
                🗑
              </Button>
            </div>
          ))}
          <Button disabled={!canEditContent}
            variant="secondary"
            size="sm"
            onClick={() => {
              const racks = config.globalRacks || [];
              const newRack = { id: crypto.randomUUID(), name: `Stagebox ${racks.length + 1}` };
              updateGlobalRacks([...racks, newRack]);
            }}
          >
            {t('configAudioBackline.addRack')}
          </Button>
        </div>

        {/* Sidefills — shared monitors for the whole band */}
        <div className="form-section">
          <h2>{t('configAudioBackline.sidefills')}</h2>
          <p style={{ marginTop: -8, marginBottom: 12, color: 'var(--text-dim)', fontSize: '0.85rem' }}>
            {t('configAudioBackline.sidefillsHint')}
          </p>
          {(specs.sidefills || []).map((sf) => (
            <div key={sf.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
              <input disabled={!canEditContent}
                type="text"
                value={sf.name}
                onChange={(e) => {
                  const list = (specs.sidefills || []).map((s) =>
                    s.id === sf.id ? { ...s, name: e.target.value } : s
                  );
                  updateSpecs({ [transport]: { ...specs, sidefills: list } } as any);
                }}
                placeholder={t('configAudioBackline.sidefillPh')}
                className="form-input"
                style={{ flex: 1, minWidth: 120 }}
              />
              <select disabled={!canEditContent}
                className="form-input"
                style={{ width: 160 }}
                value={sf.rackId || ''}
                onChange={(e) => {
                  const list = (specs.sidefills || []).map((s) =>
                    s.id === sf.id ? { ...s, rackId: e.target.value } : s
                  );
                  updateSpecs({ [transport]: { ...specs, sidefills: list } } as any);
                }}
              >
                <option value="">{t('configAudioBackline.rack')}</option>
                {(mergeStageRacks(config.globalRacks, config.specs, transport)).map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
              <select
                className="form-input"
                style={{ width: 160 }}
                value={sf.channel || 0}
                disabled={!canEditContent || !sf.rackId}
                onChange={(e) => {
                  const list = (specs.sidefills || []).map((s) =>
                    s.id === sf.id ? { ...s, channel: parseInt(e.target.value) || 0 } : s
                  );
                  updateSpecs({ [transport]: { ...specs, sidefills: list } } as any);
                }}
              >
                <option value={0}>—</option>
                {(() => {
                  const rack = mergeStageRacks(config.globalRacks, config.specs, transport).find((r) => r.id === sf.rackId);
                  const { outputs } = rackCapacities(rack);
                  return Array.from({ length: outputs }, (_, i) => i + 1).map((ch) => {
                    const occupant = isChannelTaken(usage, 'outputs', sf.rackId || '', ch, sf.rackId, sf.channel);
                    return (
                      <option key={ch} value={ch} disabled={!!occupant}>
                        {occupant ? `${ch} (${occupant})` : `${ch}`}
                      </option>
                    );
                  });
                })()}
              </select>
              <Button disabled={!canEditContent}
                variant="ghost"
                size="sm"
                onClick={async () => {
                  if (!(await showConfirm(t('configAudioBackline.removeSidefillConfirm')))) return;
                  const list = (specs.sidefills || []).filter((s) => s.id !== sf.id);
                  updateSpecs({ [transport]: { ...specs, sidefills: list } } as any);
                }}
              >
                🗑
              </Button>
            </div>
          ))}
          <Button disabled={!canEditContent}
            variant="secondary"
            size="sm"
            onClick={() => {
              const list = specs.sidefills || [];
              const n = list.length + 1;
              updateSpecs({ [transport]: { ...specs, sidefills: [...list, { id: crypto.randomUUID(), name: `Sidefill ${n}` }] } } as any);
            }}
          >
            {t('configAudioBackline.addSidefill')}
          </Button>
        </div>

        {/* Technical Specifications — reorderable per the Rider Layout */}
        {sortedSpecBlocks.map((b) => b.render())}
      </div>
    </div>
  );
}
